/**
 * Centralized API configuration for the Belhop frontend
 * All API-related configuration should be managed through this file
 */

// Environment checks
export const isDevelopment = process.env.NODE_ENV === 'development';
export const isProduction = process.env.NODE_ENV === 'production';

// Get API URL - use relative URLs when proxy is available
const getBaseUrl = (): string => {
  // In development, use proxy (relative URLs)
  if (isDevelopment) {
    return '';
  }

  // In production, check if REACT_APP_API_URL is explicitly set (including empty string for ALB routing)
  if (process.env.REACT_APP_API_URL !== undefined) {
    return process.env.REACT_APP_API_URL;
  }

  // Fallback for local development builds
  return 'http://localhost:8000';
};

export const API_BASE_URL = getBaseUrl();

// API endpoints configuration
export const API_ENDPOINTS = {
  // Base API URL
  BASE: API_BASE_URL,

  // API versioned base
  V1: `${API_BASE_URL}/api/v1`,

  // Static file URLs
  UPLOADS: `${API_BASE_URL}/uploads`,
  PROFILE_PICTURES: `${API_BASE_URL}/uploads/profile-pictures`,
  PROPERTY_IMAGES: `${API_BASE_URL}/uploads/property-images`,
};

// Axios default configuration
export const AXIOS_CONFIG = {
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // 60 seconds - increased to handle slow backend responses
};

// Helper function to construct full API URLs
export const getApiUrl = (endpoint: string): string => {
  if (endpoint.startsWith('http')) {
    return endpoint; // Already a full URL
  }
  if (endpoint.startsWith('/')) {
    // In development with proxy, return as-is; in production, prepend base URL
    return isDevelopment ? endpoint : `${API_BASE_URL}${endpoint}`;
  }
  return isDevelopment ? `/${endpoint}` : `${API_BASE_URL}/${endpoint}`;
};

// Helper function to construct upload URLs
export const getUploadUrl = (path: string): string => {
  if (!path) return '';
  if (path.startsWith('http')) {
    return path; // Already a full URL
  }
  if (path.startsWith('/')) {
    // In development with proxy, return as-is; in production, prepend base URL
    return isDevelopment ? path : `${API_BASE_URL}${path}`;
  }
  return isDevelopment ? `/uploads/${path}` : `${API_ENDPOINTS.UPLOADS}/${path}`;
};

// Export configuration object for easy access
const apiConfig = {
  API_BASE_URL,
  API_ENDPOINTS,
  AXIOS_CONFIG,
  getApiUrl,
  getUploadUrl,
  isDevelopment,
  isProduction,
};

export default apiConfig;
